import { getLocationBySlug, isLocationQualified, SupportedLocation } from "./locationRegistry";

export interface IndustryRecord {
  slug: string;
  name: string;
  iconName: string;
  shortDescription: string;
  fullDescription: string;
  primaryEmployers: string[];
  entryPathways: string[];
  hiringSignalIndicators: string[];
  outreachTips: string[];
  sampleSearchCategory: string;
  estimatedBusinessDensity: string;
  relatedIndustrySlugs: string[];
  relatedGuideSlugs: string[];
}

export const INDUSTRY_REGISTRY: Record<string, IndustryRecord> = {
  healthcare: {
    slug: "healthcare",
    name: "Healthcare & Private Practices",
    iconName: "Activity",
    shortDescription: "Discover independent outpatient clinics, dental practices, physical therapy centers, and medical groups.",
    fullDescription: "The local healthcare sector extends far beyond large hospital networks. Independent outpatient clinics, physical therapy practices, dental groups, urgent care providers, and specialized medical offices represent stable, growing local employers that frequently hire administrative, clinical, and support staff through direct contact.",
    primaryEmployers: [
      "Independent Dental Practices & Orthodontics",
      "Outpatient Physical & Occupational Therapy Clinics",
      "Private Podiatry & Dermatology Practices",
      "Community Urgent Care & Family Medicine Offices",
      "Home Health Agencies & Rehabilitation Providers",
      "Veterinary Hospitals & Animal Clinics"
    ],
    entryPathways: [
      "Medical Assistant & Front-Desk Coordinator",
      "Patient Intake & Billing Specialist",
      "Dental Assistant & Office Receptionist",
      "Physical Therapy Aide / Rehabilitation Assistant",
      "Veterinary Technician & Client Service Representative"
    ],
    hiringSignalIndicators: [
      "New provider additions listed on clinic websites",
      "Extended operating hours or new secondary facility locations",
      "Recent patient review surges mentioning high visit volume",
      "Website updates advertising new specialized treatment services"
    ],
    outreachTips: [
      "Direct your inquiry to the Practice Manager or Office Administrator rather than general contact forms.",
      "Highlight certifications (BLS, CMA, RDA, HIPAA compliance) upfront in your introductory pitch.",
      "Emphasize reliability, patient privacy awareness, and familiarity with Electronic Health Records (EHR) systems."
    ],
    sampleSearchCategory: "Medical Clinics & Health Services",
    estimatedBusinessDensity: "High (15-25% of commercial service listings)",
    relatedIndustrySlugs: ["office-administration", "technology"],
    relatedGuideSlugs: ["how-to-ask-if-a-business-is-hiring", "how-to-email-a-business-about-a-job"]
  },
  construction: {
    slug: "construction",
    name: "Construction, Trades & Contracting",
    iconName: "Hammer",
    shortDescription: "Explore HVAC contractors, electrical specialists, roofing companies, plumbers, and general builders.",
    fullDescription: "Trade contractors and specialized trade businesses form the backbone of local commercial expansion. Residential and commercial HVAC contractors, electrical service providers, plumbing firms, roofing companies, and excavators maintain steady field and office operations with frequent demand for technicians, apprentices, project estimators, and dispatchers.",
    primaryEmployers: [
      "HVAC & Commercial Refrigeration Contractors",
      "Electrical Services & Solar Installation Contractors",
      "Plumbing, Piping & Utility Contractors",
      "Roofing, Siding & Exterior Contracting Firms",
      "Residential Remodeling & General Contracting Companies",
      "Civil Excavation & Paving Companies"
    ],
    entryPathways: [
      "Apprentice Technician (Electrical, HVAC, Plumbing)",
      "Service Dispatcher & Customer Service Representative",
      "Project Estimator Assistant & CAD Drafter",
      "Warehouse Inventory & Material Handler",
      "Safety Coordinator & Field Support Specialist"
    ],
    hiringSignalIndicators: [
      "Recent commercial permit activity or fleet vehicle expansion",
      "Active customer review momentum highlighting fast response times",
      "Sponsorship of local trade school events or job fairs",
      "Website updates highlighting new commercial service lines"
    ],
    outreachTips: [
      "Call or visit early in the morning (between 7:00 AM and 8:30 AM) when shop managers and dispatchers prepare field crews.",
      "Mention your OSHA certifications, clean driving record, or trade coursework right away.",
      "Focus on willingness to learn, punctuality, and hands-on problem-solving skills."
    ],
    sampleSearchCategory: "Contractors & Trade Services",
    estimatedBusinessDensity: "High (20-30% of local trade listings)",
    relatedIndustrySlugs: ["manufacturing", "home-services"],
    relatedGuideSlugs: ["walk-in-job-application-tips", "how-to-find-a-job-with-no-experience"]
  },
  retail: {
    slug: "retail",
    name: "Specialty Retail & Local Merchants",
    iconName: "ShoppingBag",
    shortDescription: "Locate independent boutiques, hardware suppliers, specialty grocers, and commercial trade retailers.",
    fullDescription: "Beyond national big-box chains, local specialty retailers—such as independent building supply yards, high-end furniture showrooms, specialized bicycle shops, specialty food markets, and commercial equipment dealers—offer engaging, customer-focused careers with opportunities for advancement into store management and purchasing.",
    primaryEmployers: [
      "Independent Building & Hardware Merchants",
      "Specialty Outdoor Equipment & Bicycle Retailers",
      "Local Showrooms (Lighting, Flooring, Appliances)",
      "Specialty Food, Bakery & Gourmet Markets",
      "Commercial Equipment & Uniform Suppliers"
    ],
    entryPathways: [
      "Sales Associate & Customer Specialist",
      "Inventory & Receiving Assistant",
      "Store Merchandiser & Display Assistant",
      "E-commerce Coordinator & Social Media Manager",
      "Assistant Store Manager / Keyholder"
    ],
    hiringSignalIndicators: [
      "Store expansions or new showroom location openings",
      "Seasonal inventory updates and holiday preparation announcements",
      "Increased social media activity showcasing custom products",
      "Physical store remodeling or signage updates"
    ],
    outreachTips: [
      "Visit in person during quiet weekday morning hours when store managers are accessible.",
      "Show genuine enthusiasm for the store's specific product line or community reputation.",
      "Bring a printed one-page resume and ask directly for the General Manager."
    ],
    sampleSearchCategory: "Retail & Local Goods",
    estimatedBusinessDensity: "Moderate-High (15-20% of retail listings)",
    relatedIndustrySlugs: ["restaurants", "home-services"],
    relatedGuideSlugs: ["walk-in-job-application-tips", "first-resume-with-no-experience"]
  },
  restaurants: {
    slug: "restaurants",
    name: "Hospitality, Dining & Catering",
    iconName: "Utensils",
    shortDescription: "Find independent restaurants, catering groups, coffee roasters, and local hospitality businesses.",
    fullDescription: "Independent dining groups, farm-to-table restaurants, artisan bakeries, catering providers, and specialty coffee roasters are vibrant local employers. They value adaptability, speed, customer care, and teamwork, offering clear pathways from entry-level hospitality into kitchen management and operations.",
    primaryEmployers: [
      "Independent Family Restaurants & Bistro Groups",
      "Event Catering Companies & Banquet Halls",
      "Artisan Bakeries & Specialty Coffee Roasters",
      "Craft Breweries & Taprooms with Food Service",
      "Corporate Dining Services & Private Clubs"
    ],
    entryPathways: [
      "Barista, Server, or Host / Hostess",
      "Prep Cook & Line Cook Apprentice",
      "Catering Assistant & Event Staff",
      "Shift Supervisor & Assistant Floor Manager",
      "Social Media & Event Marketing Coordinator"
    ],
    hiringSignalIndicators: [
      "Outdoor patio expansions or private dining room additions",
      "New seasonal menu launches or catering service extensions",
      "Consistent high volume customer review growth",
      "Participation in local food festivals and community events"
    ],
    outreachTips: [
      "Never visit between 11:30 AM – 2:00 PM or 5:00 PM – 8:30 PM (peak dining hours). Visit weekdays between 2:30 PM – 4:00 PM.",
      "Ask politely to speak with the General Manager or Executive Chef.",
      "Highlight your Food Handler certifications, team attitude, and schedule flexibility."
    ],
    sampleSearchCategory: "Restaurants & Catering",
    estimatedBusinessDensity: "High (20-25% of service listings)",
    relatedIndustrySlugs: ["retail", "hospitality"],
    relatedGuideSlugs: ["how-to-ask-if-a-business-is-hiring", "walk-in-job-application-tips"]
  },
  manufacturing: {
    slug: "manufacturing",
    name: "Manufacturing, Machining & Engineering",
    iconName: "Factory",
    shortDescription: "Connect with precision machine shops, fabrication facilities, component assemblers, and industrial suppliers.",
    fullDescription: "Local manufacturing goes far beyond massive assembly plants. Precision CNC machine shops, custom metal fabricators, electronics assemblers, packaging suppliers, and industrial repair facilities operate quietly in local industrial parks, offering skilled trade and technical employment.",
    primaryEmployers: [
      "CNC Machining & Tooling Companies",
      "Sheet Metal Fabrication & Welding Shops",
      "Electronics & Cable Harness Assembly Plants",
      "Industrial Hydraulics & Pneumatics Repair Shops",
      "Custom Packaging & Plastics Molding Facilities"
    ],
    entryPathways: [
      "Machine Operator Apprentice / CNC Loader",
      "Quality Control Inspector & Inspector Assistant",
      "Assembly Technician & Soldering Specialist",
      "Shipping, Receiving & Warehouse Coordinator",
      "Maintenance Technician Assistant"
    ],
    hiringSignalIndicators: [
      "ISO certification updates or plant equipment upgrades",
      "New facility acquisitions in local industrial parks",
      "Subcontractor supplier announcements for major regional OEMs",
      "Website highlights of expanded precision machining capabilities"
    ],
    outreachTips: [
      "Send a concise email or call the Operations Manager / Shop Foreman.",
      "Highlight Blueprint Reading skills, Math competencies, or CAD software familiarity.",
      "Emphasize attention to safety guidelines, measurement precision, and attendance reliability."
    ],
    sampleSearchCategory: "Manufacturing & Industrial Facilities",
    estimatedBusinessDensity: "Moderate (10-15% of industrial listings)",
    relatedIndustrySlugs: ["construction", "technology"],
    relatedGuideSlugs: ["how-to-email-a-business-about-a-job", "how-to-find-small-business-jobs"]
  },
  "office-administration": {
    slug: "office-administration",
    name: "Professional Services & Law",
    iconName: "Building2",
    shortDescription: "Identify law firms, accounting agencies, real estate firms, insurance brokerages, and corporate services.",
    fullDescription: "Local professional services firms—including law practices, accounting and CPA offices, independent insurance brokerages, real estate agencies, and financial planning firms—require organized, articulate administrative and client-support staff to manage day-to-day operations.",
    primaryEmployers: [
      "Independent Law Practices (Litigation, Real Estate, Estate Planning)",
      "Accounting, CPA & Bookkeeping Agencies",
      "Independent Commercial Real Estate & Property Management Firms",
      "Insurance Agencies & Risk Management Brokers",
      "Title Companies & Financial Advisory Groups"
    ],
    entryPathways: [
      "Legal Receptionist & File Clerk",
      "Office Assistant & Administrative Coordinator",
      "Bookkeeping Assistant & Accounts Receivable Clerk",
      "Client Intake Specialist & Customer Coordinator",
      "Marketing & Document Specialist"
    ],
    hiringSignalIndicators: [
      "New partner announcements or associate attorney additions",
      "Office relocation to larger professional suites",
      "Redesigned corporate website with client portal features",
      "Active local chamber of commerce sponsorship"
    ],
    outreachTips: [
      "Submit a polished cover letter and clean PDF resume directly to the Firm Administrator or Office Manager.",
      "Demonstrate impeccable written communication, proofreading, and software literacy (MS Office, Google Workspace).",
      "Highlight discretion, organizational accuracy, and professional phone etiquette."
    ],
    sampleSearchCategory: "Law Practices & Professional Offices",
    estimatedBusinessDensity: "High (15-20% of commercial listings)",
    relatedIndustrySlugs: ["healthcare", "technology"],
    relatedGuideSlugs: ["how-to-email-a-business-about-a-job", "how-to-contact-a-local-employer"]
  },
  technology: {
    slug: "technology",
    name: "IT, Managed Services & Tech",
    iconName: "Laptop",
    shortDescription: "Discover Managed Service Providers (MSPs), IT support shops, web agencies, and local tech integration firms.",
    fullDescription: "Every community relies on local technology service providers. Managed Service Providers (MSPs), cybersecurity consultants, network cabling installers, custom web development agencies, and audio-visual integrators provide critical tech infrastructure for local businesses and schools.",
    primaryEmployers: [
      "Managed Service Providers (MSPs) & IT Support Agencies",
      "Cybersecurity & Network Cabling Contractors",
      "Local Web Development & Digital Marketing Agencies",
      "Commercial Audio-Visual (AV) & Security System Installers",
      "Custom Software & Database Consulting Firms"
    ],
    entryPathways: [
      "Helpdesk Tier 1 Support Specialist",
      "Field IT Technician & Cabling Assistant",
      "Junior Web Developer / CMS Content Manager",
      "Client Onboarding Coordinator",
      "AV Installation Assistant"
    ],
    hiringSignalIndicators: [
      "New client partnership announcements on company blogs",
      "Certifications earned (CompTIA, Microsoft Partner, Cisco)",
      "Website redesigns featuring expanded service packages",
      "Active local tech meetup hosting or involvement"
    ],
    outreachTips: [
      "Email the Technical Director or Owner directly with a short summary of home lab projects or certifications.",
      "Include links to a GitHub profile, personal website, or LinkedIn profile.",
      "Emphasize customer service attitude alongside technical troubleshooting skills."
    ],
    sampleSearchCategory: "IT Services & Technology Providers",
    estimatedBusinessDensity: "Moderate (8-12% of commercial listings)",
    relatedIndustrySlugs: ["office-administration", "manufacturing"],
    relatedGuideSlugs: ["how-to-email-a-business-about-a-job", "how-to-find-hidden-jobs"]
  },
  automotive: {
    slug: "automotive",
    name: "Automotive Repair & Services",
    iconName: "Car",
    shortDescription: "Locate independent auto repair shops, body shops, tire centers, and fleet maintenance shops.",
    fullDescription: "Independent auto repair facilities, collision centers, tire and brake shops, and fleet maintenance providers keep local transportation running. These businesses value mechanical aptitude, diagnostic problem-solving, customer communication, and reliable work ethics.",
    primaryEmployers: [
      "Independent Auto Repair & Service Centers",
      "Collision Repair & Auto Body Shops",
      "Tire, Brake & Suspension Specialty Centers",
      "Fleet Maintenance & Commercial Vehicle Repair Shops",
      "Custom Auto Detailers & Performance Shops"
    ],
    entryPathways: [
      "Lube & Tire Technician / Apprentice",
      "Service Writer / Customer Service Advisor",
      "Auto Body Apprentice / Prep Technician",
      "Parts Counter Specialist & Inventory Clerk",
      "Shop Helper & Detail Technician"
    ],
    hiringSignalIndicators: [
      "Addition of new service bays or vehicle lift equipment",
      "ASE certification badges and master tech announcements",
      "Strong review volume praising honest estimates and fast turnarounds",
      "Expansion into fleet maintenance contracts"
    ],
    outreachTips: [
      "Visit shop managers in person between 9:00 AM – 11:00 AM.",
      "Highlight ASE certifications, tools owned, clean driving record, and physical stamina.",
      "Show eagerness to learn advanced diagnostic software and EV/hybrid service techniques."
    ],
    sampleSearchCategory: "Automotive Repair & Service Facilities",
    estimatedBusinessDensity: "Moderate-High (10-15% of local listings)",
    relatedIndustrySlugs: ["construction", "manufacturing"],
    relatedGuideSlugs: ["walk-in-job-application-tips", "how-to-ask-if-a-business-is-hiring"]
  }
};

export function getAllIndustries(): IndustryRecord[] {
  return Object.values(INDUSTRY_REGISTRY);
}

export function getIndustryBySlug(slug: string): IndustryRecord | null {
  if (!slug) return null;
  const cleanSlug = slug.trim().toLowerCase();
  return INDUSTRY_REGISTRY[cleanSlug] || null;
}

export interface QualifiedIndustryLocationMatch {
  industry: IndustryRecord;
  location: SupportedLocation;
  citySlug: string;
  industrySlug: string;
  targetPath: string;
}

export function getQualifiedIndustryLocation(
  industrySlug: string,
  citySlug: string
): QualifiedIndustryLocationMatch | null {
  const industry = getIndustryBySlug(industrySlug);
  const location = getLocationBySlug(citySlug);

  if (!industry || !location || !isLocationQualified(location)) {
    return null;
  }

  return {
    industry,
    location,
    citySlug: location.citySlug,
    industrySlug: industry.slug,
    targetPath: `/jobs/${industry.slug}/${location.citySlug}`
  };
}
